import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useEvent } from '@/lib/events'
import { useAuth } from '@/hooks/useAuth'

interface AcornContextValue {
  balance: number
  isLoading: boolean
  refresh: () => Promise<void>
}

const AcornContext = createContext<AcornContextValue>({
  balance: 0,
  isLoading: true,
  refresh: async () => {},
})

export function AcornProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [balance, setBalance] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const refresh = async () => {
    if (!user) return
    const { data } = await supabase
      .from('profiles')
      .select('acorn_balance')
      .eq('id', user.id)
      .single()
    if (data) setBalance((data as { acorn_balance: number }).acorn_balance ?? 0)
  }

  useEffect(() => {
    if (!user) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    refresh().finally(() => setIsLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // React to acorns.awarded event — update balance immediately and show toast
  useEvent('acorns.awarded', ({ newBalance, amount }) => {
    setBalance(newBalance)
    if (amount === 1) {
      toast('🌰 +1 acorn')
    } else {
      toast(`🌰 +${amount} acorns`)
    }
  })

  return (
    <AcornContext.Provider value={{ balance, isLoading, refresh }}>
      {children}
    </AcornContext.Provider>
  )
}

export function useAcornBalance() {
  return useContext(AcornContext)
}
